import {nextTick} from 'vue';
import axios from 'axios';
import {globalActions} from '../store/global-actions';
import {Cookies} from '../shared/cookies-extended';
import _u from '../shared/utils';

const contactModes = Object.freeze({message: 0, phone: 1, adverts: 2});

const listingContactForm = {
    template: '#listing-contact-form',
    props: {
        isCompact: {type: Boolean},
        listing: {required: true},
        trackUrl: {type: String},
        loadPhoneUrl: {type: String},
        labels: {},
        infoMessages: {},
        classes: {default: {}},
        userInfo: {required: true},
        isProperty: {required: true},
        hasEngage: {type: Boolean},
        forceHideText: {type: Boolean},
        textSize: {type: String},
        preferContactByPhone: {default: true},
        userMatchedAdvertsUrl: {type: String},
        contactTypes: {required: true},
        contactModalId: {
            type: String,
            required: false,
        },
        contactAttemptButtonId: {
            type: String,
            required: false,
        },
    },
    data() {
        return {
            isTextVisible: false,
            isPhoneVisible: false,
            phoneNumber: null,
            isUserAuthenticated: this.userInfo.isAuthenticated,
            isUserEmailConfirmed: this.userInfo.isEmailConfirmed,
            message: null,
            cookieContactFormWasHidden: 'Contact-Form-Was-Hidden',
            cookieExpirationMs: 10 * 60 * 1000,
            isSendDisabled: false,
            mode: this.preferContactByPhone ? contactModes.phone : contactModes.message,
            recommendedAdverts: [],
            advertsCollapsedAmount: 3,
            advertsLoaded: false,
            isAllAdvertVisible: false,
            isLimitModalOpened: false,
            listingContactTypes: this.contactTypes.reduce((result, i) => {
                const key = i.label.charAt(0).toLowerCase() + i.label.slice(1);
                result[key] = i.value;
                return result;
            }, {}),
            wasContactFormHiddenCookies: null,
            observer: null,
            isFormVisible: false,
        };
    },
    computed: {
        buttonLabel() {
            return !this.message ? this.listing.contactButtonMessage : this.labels.activeButtonLabel;
        },

        hasRecommendedAdverts() {
            return this.recommendedAdverts && this.recommendedAdverts.length > 0;
        },

        hasPhoneTab() {
            return this.listing.forceShowPhoneNumber;
        },

        hasTabs() {
            return this.hasRecommendedAdverts || this.hasPhoneTab;
        },

        visibleRecommendedAdverts() {
            return this.isAllAdvertVisible ? this.recommendedAdverts : this.recommendedAdverts.slice(0, this.advertsCollapsedAmount);
        },

        isContactFormVisible() {
            return (this.isTextVisible || this.forceHideText || this.isCompact) && this.isFormVisible;
        },

        isPriceHidden() {
            return this.listing.hidePrice;
        },

        isSendButtonHidden() {
            return !this.message && !this.isPriceHidden;
        },
    },
    watch: {
        forceHideText(val) {
            if (!this.wasContactFormHiddenCookies) {
                this.isTextVisible = !val;
            }
        },

        isCompact(val) {
            this.isTextVisible = !val;
        },

        isTextVisible(val) {
            this.isFormVisible = val;
        },

        isContactFormVisible(val) {
            if (val) {
                this._tryLoadMatchedAdverts();
            }
        },
    },
    methods: {
        async sendMatchedAsync(advert) {
            await this.sendMessageAsync(advert.message, {
                type: this.listingContactTypes.sendMatched,
                info: {
                    listingId: advert.id,
                    listingType: advert.listingType,
                },
            }, false);
        },

        async contactOwnerAsync(message) {
            if (message && message !== '') {
                await this.sendMessageAsync(message, {type: this.listingContactTypes.chat});
            } else if (this.isPriceHidden) {
                await this.sendMessageAsync(this.infoMessages.askPriceMessage, {type: this.listingContactTypes.chat});
            } else {
                this._focusTextarea();
            }
        },

        async sendMessageAsync(message, listingAction, checkMessageLimit = true) {
            if (!this.isUserAuthenticated) {
                globalActions.changePreselectRegistrationReason(globalActions.getMessageRegistrationPlace());
                globalActions.openLoginModal();
            } else if (!this.isUserEmailConfirmed) {
                globalActions.openInfoModal(this.infoMessages.emailNotConfirmedTitle, this.infoMessages.emailNotConfirmedMessage);
            } else {
                const chat = window.chat;

                if (chat) {
                    this.isSendDisabled = true;
                    chat.onConversationOpening = async (convId) => {
                        chat.currentMessage = message;
                        await chat.sendDataAsync(convId, checkMessageLimit, this.contactModalId,
                            {actionType: listingAction.type, actionInfo: listingAction.info});
                        chat.currentConversation = null;
                        chat.onConversationOpening = async () => {};
                    };
                    await chat.initiateConversation(this.listing.id, this.listing.convType);

                    this.message = null;
                    this._resetTextareaHeight();
                    this.isSendDisabled = false;
                }
            }
            this.hideIfCompact();
        },

        showPhone() {
            if (this.phoneNumber != null) {
                this.isPhoneVisible = true;
            } else {
                axios.post(this.loadPhoneUrl)
                    .then((response) => {
                        this.phoneNumber = response.data;
                        this.isPhoneVisible = true;
                    })
                    .catch((error) => {
                        if (error.response && error.response.status === 403) {
                            globalActions.openLimitInfoModal();
                        } else {
                            globalActions.handleAjaxErrors(error);
                        }
                    });
            }
        },

        async sendRequestNumberAsync(propertyMessage, inquiryMessage) {
            if (propertyMessage && inquiryMessage) {
                await this.sendMessageAsync(this.isProperty ? propertyMessage : inquiryMessage, {type: this.listingContactTypes.phone});
            }
        },

        async switchTextareaVisibilityAsync() {
            if (this.isTextVisible) {
                this._hideTextarea();

                if (!this._wasContactFormHidden()) {
                    const expiration = new Date(new Date().getTime() + this.cookieExpirationMs);
                    await Cookies.setWithBackupAsync(this.cookieContactFormWasHidden, true, {expires: expiration});
                    await this._refreshCookiesStateAsync();
                }
            } else {
                this._showTextarea();
            }
        },

        hideIfCompact() {
            if (this.isTextVisible) {
                this._hideTextarea();
            }
        },

        changeMode(mode) {
            this.mode = mode;
        },

        setMessageMode() {
            this.changeMode(contactModes.message);
            this._showTextarea();
        },

        toggleAdvertsList() {
            this.isAllAdvertVisible = true;
        },

        collapseAdvertsList() {
            this.isAllAdvertVisible = false;
        },

        _resetTextareaHeight() {
            if (this.mode === contactModes.message) {
                this.$refs.textarea.resetHeight();
            }
        },

        _showTextarea() {
            this.isTextVisible = true;
            this._focusTextarea();
        },

        _hideTextarea() {
            this.isTextVisible = false;
        },

        _focusTextarea() {
            nextTick(() => {
                if (this.mode === contactModes.message) {
                    this.$el.querySelector('textarea').focus();
                }
            });
        },

        _wasContactFormHidden() {
            return Cookies.get(this.cookieContactFormWasHidden) != null;
        },

        _getMatchedAdverts() {
            const okStatus = 200;
            axios.get(this.userMatchedAdvertsUrl)
                .then((res) => {
                    if (res.status === okStatus) {
                        this.recommendedAdverts = res.data;
                        this.mode = contactModes.adverts;
                        this.removeListeners();
                    }
                })
                .catch((err) => globalActions.handleAjaxErrors(err))
                .finally(this.tryOpenDropdown);
        },

        _getMatchedAdvertsIfVisible() {
            if (this._shouldLoadAdverts()) {
                this.advertsLoaded = true;
                this._getMatchedAdverts();
            }
        },

        _shouldLoadAdverts() {
            return this.isContactFormVisible && !this.advertsLoaded;
        },

        _tryLoadMatchedAdverts() {
            if (this._shouldLoadAdverts()) {
                this._getMatchedAdverts();
            } else {
                window.addEventListener('resize', this._getMatchedAdvertsIfVisible);
                document.getElementById('search-results-container').addEventListener('scroll', this._getMatchedAdvertsIfVisible);
            }
        },

        onFormVisibilityObserved(entries) {
            entries.forEach(({target, isIntersecting}) => {
                if (!isIntersecting) {
                    return;
                }

                if (_u.isFullVisibilityExpected(target)) {
                    this.isFormVisible = true;
                    this.tryOpenDropdown();
                    this.observer.unobserve(target);
                }
            });
        },

        tryOpenDropdown() {
            if (!this.isTextVisible) {
                this.isTextVisible = !this.isCompact && !this.forceHideText;
            }
        },

        async _refreshCookiesStateAsync() {
            this.wasContactFormHiddenCookies = await Cookies.getWithBackupCheckAsync(this.cookieContactFormWasHidden) != null;
        },

        removeListeners() {
            window.removeEventListener('resize', this._getMatchedAdvertsIfVisible);
            document.getElementById('search-results-container').removeEventListener('scroll', this._getMatchedAdvertsIfVisible);
            this.observer.disconnect();
        },
    },
    created() {
        this.observer = new IntersectionObserver(
            this.onFormVisibilityObserved,
            {
                threshold: 1.0,
            },
        );

        if (!this.isUserAuthenticated || !this.isUserEmailConfirmed) {
            this.tryOpenDropdown();
        }
        if (this.wasContactFormHiddenCookies) {
            this.isTextVisible = false;
        }
    },
    async mounted() {
        await this._refreshCookiesStateAsync();
        this.observer.observe(this.$el);
    },
};

function initListingContactForm(app) {
    app.component('listing-contact-form', listingContactForm);
};

export default initListingContactForm;
